'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  ArrowLeft,
  Briefcase,
  Rocket,
  Heart,
  Users,
  ChevronRight,
  Sparkles,
  Brain,
  Target
} from 'lucide-react';

export default function CareersPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="container max-w-6xl mx-auto px-4 py-4">
          <Link href="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-950 dark:via-blue-950/20 dark:to-purple-950/20" />
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-5" />

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-full mb-6">
            <Briefcase className="w-4 h-4" />
            <span className="text-sm font-medium">Careers at Apprendo</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-blue-900 dark:from-white dark:to-blue-200">
            Help Us Amplify Breakthrough Moments
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-3xl mx-auto">
            Join a team that's helping the world's most innovative R&D teams share their genius.
          </p>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Join Apprendo?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Be part of something bigger than software
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-6">
              <Rocket className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-3" />
              <h3 className="font-semibold mb-2">Mission That Matters</h3>
              <p className="text-sm text-muted-foreground">
                Help breakthrough innovations reach the world. From cancer treatments to quantum computers, your work enables progress.
              </p>
            </Card>

            <Card className="p-6">
              <Brain className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-3" />
              <h3 className="font-semibold mb-2">Technical Excellence</h3>
              <p className="text-sm text-muted-foreground">
                Work with cutting-edge AI, video processing, and real-time systems. Push the boundaries of what's possible.
              </p>
            </Card>

            <Card className="p-6">
              <Heart className="w-8 h-8 text-red-600 dark:text-red-400 mb-3" />
              <h3 className="font-semibold mb-2">Culture of Care</h3>
              <p className="text-sm text-muted-foreground">
                Remote-first, async-friendly, human-centered. We build products and teams with empathy and intention.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* We're Not Hiring Yet - But We Want Your Story */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              We're Not Hiring Yet—But We Want to Know You
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              We're heads down building something revolutionary. While we're not ready to grow the team just yet, we believe the best hires come from connections made early. Share your story with us.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="p-8 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Why Connect Now?</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span>Be first to know when we open positions that match your expertise</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Join our community of mission-driven builders and thinkers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Target className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Get early insights into what we're building and why it matters</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Brain className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <span>Shape our culture and product direction with your perspective</span>
                    </li>
                  </ul>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold mb-3">Who We'll Eventually Need</h3>
                  <p className="text-muted-foreground mb-4">
                    While we don't have open roles today, here's the kind of talent that will help us transform how R&D teams share knowledge:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Engineering & Product</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Full-stack engineers (Next.js, TypeScript)</li>
                        <li>• AI/ML specialists (NLP, video analysis)</li>
                        <li>• Real-time systems architects</li>
                        <li>• Product designers who love complex UX</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Growth & Operations</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Customer success for technical teams</li>
                        <li>• Content strategists who understand R&D</li>
                        <li>• Sales to enterprise R&D organizations</li>
                        <li>• Operations leaders who scale thoughtfully</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold mb-3">Share Your Story</h3>
                  <p className="text-muted-foreground mb-4">
                    Tell us about yourself—not just your resume, but your story. What drives you? What problems keep you up at night? Why does helping R&D teams share knowledge matter to you?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a href="mailto:stories@apprendo.co">
                      <Button size="lg" className="px-8">
                        Send Your Story
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                    <p className="text-sm text-muted-foreground flex items-center">
                      Email us at <span className="font-mono ml-2">stories@apprendo.co</span>
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground italic">
                "The best time to plant a tree was 20 years ago. The second best time is now."<br />
                <span className="not-italic">— Let's grow something meaningful together</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Benefits & Perks
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We take care of our team so they can take care of our mission
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Health & Wellness</p>
                <p className="text-sm text-muted-foreground">100% covered health, dental, vision</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Equity for Everyone</p>
                <p className="text-sm text-muted-foreground">Meaningful equity packages</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Remote-First</p>
                <p className="text-sm text-muted-foreground">Work from anywhere, async-friendly</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Learning Budget</p>
                <p className="text-sm text-muted-foreground">$2,500/year for courses and conferences</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Equipment</p>
                <p className="text-sm text-muted-foreground">Top-tier laptop and home office setup</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Time Off</p>
                <p className="text-sm text-muted-foreground">Flexible PTO + company holidays</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Culture */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Culture
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
              <Sparkles className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-3" />
              <h3 className="font-semibold mb-2">Enable Genius</h3>
              <p className="text-sm text-muted-foreground">
                We don't force square pegs into round holes. We find what makes each person brilliant and amplify it.
              </p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20">
              <Users className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-3" />
              <h3 className="font-semibold mb-2">Safe to Share</h3>
              <p className="text-sm text-muted-foreground">
                Just like our product, our culture starts with psychological safety. Ideas are welcomed, mistakes are learning.
              </p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
              <Target className="w-8 h-8 text-green-600 dark:text-green-400 mb-3" />
              <h3 className="font-semibold mb-2">Impact Focused</h3>
              <p className="text-sm text-muted-foreground">
                Every line of code, every customer call, every decision—we ask: does this help breakthroughs reach the world?
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Stay Connected
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Follow our journey as we help R&D teams build sharing cultures.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="px-8 py-6 text-lg">
              Join Our Newsletter
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="px-8 py-6 text-lg bg-transparent text-white border-white hover:bg-white hover:text-purple-600">
              Follow Our Story
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

// Add missing import
import { CheckCircle } from 'lucide-react';